import React, { useState, useEffect, useCallback } from 'react';

// ─── STORAGE ────────────────────────────────────────────────────────────────
const STORAGE_KEY = 'jobhunter_v2';

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function saveState(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
}

const DEFAULT_SOURCES = [
  { id: 'remotive', name: 'Remotive', url: 'https://remotive.com/remote-jobs' },
  { id: 'jobicy', name: 'Jobicy', url: 'https://jobicy.com' },
  { id: 'workingnomads', name: 'Working Nomads', url: 'https://workingnomads.com/jobs' },
  { id: 'dynamite', name: 'Dynamite Jobs', url: 'https://dynamitejobs.com' },
  { id: 'pharmiweb', name: 'PhArmiWeb', url: 'https://www.pharmiweb.jobs/jobs' },
  { id: 'remoterocketship', name: 'Remote Rocketship', url: 'https://remoterocketship.com' },
  { id: 'jobgether', name: 'Jobgether', url: 'https://jobgether.com' },
  { id: 'weworkremotely', name: 'We Work Remotely', url: 'https://weworkremotely.com' },
  { id: 'biospace', name: 'BioSpace', url: 'https://www.biospace.com/jobs' },
  { id: 'cips', name: 'CIPS Jobs', url: 'https://jobs.cips.org' },
  { id: 'himalayas', name: 'Himalayas', url: 'https://himalayas.app/jobs' },
  { id: 'linkedin', name: 'LinkedIn', url: 'https://www.linkedin.com/jobs' },
  { id: 'indeed', name: 'Indeed', url: 'https://es.indeed.com' },
  { id: 'infojobs', name: 'InfoJobs', url: 'https://www.infojobs.net' },
  { id: 'glassdoor', name: 'Glassdoor', url: 'https://www.glassdoor.com/Job/jobs.htm' },
];

const STATUS_COLORS = {
  nueva: '#5c8ce0', guardada: '#4caf82', aplicada: '#9c6ce0',
  en_proceso: '#e08c3c', oferta: '#2e7d52', descartada: '#e05c5c', cerrada: '#5a5a72',
};
const STATUS_LABELS = {
  nueva: 'Nueva', guardada: 'Guardada', aplicada: 'Aplicada',
  en_proceso: 'En Proceso', oferta: 'Oferta', descartada: 'Descartada', cerrada: 'Cerrada',
};

const DEFAULT_STATE = {
  jobs: [],
  sources: DEFAULT_SOURCES,
  targetCompanies: 'Tether Operations, EnnovationHUB, Trimble, GE Vernova, GE Aerospace, Porta, SGS',
};

// ─── CSS ─────────────────────────────────────────────────────────────────────
const CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #0f0f13; --surface: #17171f; --surface2: #1e1e28;
    --border: #2a2a38; --accent: #c8a96e; --text: #e8e8f0;
    --text2: #9090a8; --text3: #5a5a72; --green: #4caf82;
    --red: #e05c5c; --orange: #e08c3c; --blue: #5c8ce0; --purple: #9c6ce0;
  }
  body { background: var(--bg); color: var(--text); font-family: 'DM Sans', sans-serif; font-size: 14px; line-height: 1.6; min-height: 100vh; }
  .layout { display: flex; height: 100vh; overflow: hidden; }

  /* SIDEBAR */
  .sidebar { width: 200px; min-width: 200px; background: var(--surface); border-right: 1px solid var(--border); display: flex; flex-direction: column; height: 100vh; overflow-y: auto; }
  .sidebar-logo { padding: 20px 16px 16px; border-bottom: 1px solid var(--border); }
  .sidebar-logo h1 { font-family: 'DM Serif Display', serif; font-size: 20px; color: var(--accent); }
  .sidebar-logo p { font-size: 10px; color: var(--text3); text-transform: uppercase; letter-spacing: 1.5px; margin-top: 1px; }
  .sidebar-section { padding: 12px 8px 6px; }
  .sidebar-section-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1.5px; color: var(--text3); padding: 0 8px; margin-bottom: 3px; }
  .nav-item { display: flex; align-items: center; gap: 7px; padding: 7px 9px; border-radius: 7px; cursor: pointer; color: var(--text2); font-size: 13px; font-weight: 400; transition: all 0.15s; border: none; background: none; width: 100%; text-align: left; font-family: 'DM Sans', sans-serif; }
  .nav-item:hover { background: var(--surface2); color: var(--text); }
  .nav-item.active { background: var(--surface2); color: var(--accent); font-weight: 500; }
  .nav-badge { margin-left: auto; background: var(--accent); color: #000; font-size: 10px; font-weight: 700; padding: 1px 5px; border-radius: 8px; min-width: 16px; text-align: center; }
  .source-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; transition: background 0.3s; }

  /* MAIN */
  .main { flex: 1; display: flex; flex-direction: column; overflow: hidden; }

  /* BROWSER PANEL */
  .browser-panel { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; }
  .browser-toolbar { padding: 10px 16px; background: var(--surface); border-bottom: 1px solid var(--border); display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
  .browser-url { flex: 1; background: var(--surface2); border: 1px solid var(--border); color: var(--text2); padding: 6px 10px; border-radius: 6px; font-size: 12px; font-family: 'DM Sans', sans-serif; }
  .iframe-container { flex: 1; position: relative; background: #fff; }
  .iframe-container iframe { width: 100%; height: 100%; border: none; }
  .iframe-blocked { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; gap: 12px; color: var(--text3); text-align: center; padding: 40px; }
  .iframe-blocked h3 { font-family: 'DM Serif Display', serif; font-size: 22px; color: var(--text2); }
  .iframe-blocked p { font-size: 13px; line-height: 1.6; max-width: 360px; }

  /* SAVE BUTTON FLOATING */
  .save-fab { position: absolute; bottom: 24px; right: 24px; z-index: 100; }
  .save-fab-btn { background: var(--accent); color: #000; border: none; border-radius: 12px; padding: 12px 20px; font-size: 14px; font-weight: 600; cursor: pointer; font-family: 'DM Sans', sans-serif; box-shadow: 0 4px 20px #00000066; transition: all 0.15s; display: flex; align-items: center; gap: 8px; }
  .save-fab-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 24px #00000088; }

  /* CRM PANEL */
  .crm-panel { flex: 1; overflow-y: auto; }
  .page-header { padding: 24px 24px 0; border-bottom: 1px solid var(--border); padding-bottom: 16px; display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
  .page-title { font-family: 'DM Serif Display', serif; font-size: 24px; color: var(--text); }
  .page-subtitle { color: var(--text3); font-size: 12px; margin-top: 2px; }
  .page-content { padding: 20px 24px; }

  /* BUTTONS */
  .btn { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 7px; font-size: 13px; font-weight: 500; cursor: pointer; border: none; transition: all 0.15s; font-family: 'DM Sans', sans-serif; }
  .btn-primary { background: var(--accent); color: #000; }
  .btn-primary:hover { opacity: 0.88; }
  .btn-ghost { background: var(--surface2); color: var(--text2); border: 1px solid var(--border); }
  .btn-ghost:hover { color: var(--text); }
  .btn-danger { background: transparent; color: var(--red); border: 1px solid var(--red); }
  .btn-danger:hover { background: var(--red); color: #fff; }
  .btn-sm { padding: 5px 10px; font-size: 12px; }

  /* JOB CARDS */
  .jobs-list { display: flex; flex-direction: column; gap: 10px; }
  .job-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 14px 16px; transition: all 0.15s; cursor: pointer; }
  .job-card:hover { border-color: var(--text3); }
  .job-card.target-company { border-color: var(--accent); }
  .job-card-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin-bottom: 8px; }
  .job-title { font-size: 14px; font-weight: 500; color: var(--text); line-height: 1.3; }
  .job-company { font-size: 12px; color: var(--text2); margin-top: 2px; }
  .job-meta { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 8px; }
  .tag { font-size: 11px; padding: 2px 7px; border-radius: 4px; font-weight: 500; }
  .tag-source { background: var(--surface2); color: var(--text3); border: 1px solid var(--border); }
  .tag-status { color: #fff; }
  .tag-target { background: #2a1f0a; color: var(--accent); border: 1px solid #c8a96e44; }
  .job-actions { display: flex; gap: 6px; margin-top: 10px; padding-top: 10px; border-top: 1px solid var(--border); }
  .status-select { flex: 1; background: var(--surface2); border: 1px solid var(--border); color: var(--text); padding: 5px 8px; border-radius: 6px; font-size: 12px; cursor: pointer; font-family: 'DM Sans', sans-serif; }

  /* PIPELINE TABS */
  .pipeline-tabs { display: flex; gap: 4px; margin-bottom: 16px; background: var(--surface); border: 1px solid var(--border); border-radius: 9px; padding: 3px; width: fit-content; flex-wrap: wrap; }
  .pipeline-tab { padding: 5px 12px; border-radius: 6px; font-size: 12px; font-weight: 500; cursor: pointer; border: none; background: none; color: var(--text3); transition: all 0.15s; font-family: 'DM Sans', sans-serif; }
  .pipeline-tab.active { background: var(--surface2); color: var(--text); }
  .status-dot { width: 6px; height: 6px; border-radius: 50%; display: inline-block; margin-right: 4px; }

  /* STATS */
  .stats-row { display: flex; gap: 10px; margin-bottom: 16px; flex-wrap: wrap; }
  .stat-card { background: var(--surface); border: 1px solid var(--border); border-radius: 9px; padding: 12px 16px; flex: 1; min-width: 80px; }
  .stat-value { font-family: 'DM Serif Display', serif; font-size: 26px; color: var(--accent); line-height: 1; }
  .stat-label { font-size: 10px; color: var(--text3); text-transform: uppercase; letter-spacing: 1px; margin-top: 3px; }

  /* MODAL */
  .modal-overlay { position: fixed; inset: 0; background: #00000088; z-index: 500; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .modal { background: var(--surface); border: 1px solid var(--border); border-radius: 14px; width: 100%; max-width: 540px; max-height: 88vh; overflow-y: auto; padding: 24px; }
  .modal-title { font-family: 'DM Serif Display', serif; font-size: 20px; color: var(--text); margin-bottom: 3px; }
  .modal-section { margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--border); }
  .modal-section-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: var(--text3); margin-bottom: 5px; font-weight: 600; }
  .input, .textarea { width: 100%; background: var(--surface2); border: 1px solid var(--border); color: var(--text); padding: 8px 10px; border-radius: 7px; font-size: 13px; font-family: 'DM Sans', sans-serif; transition: border-color 0.15s; }
  .textarea { min-height: 70px; resize: vertical; line-height: 1.5; }
  .input:focus, .textarea:focus { outline: none; border-color: var(--accent); }
  .field-group { margin-bottom: 12px; }
  .field-label { font-size: 11px; color: var(--text2); font-weight: 500; margin-bottom: 4px; display: block; }

  /* TOOLBAR */
  .toolbar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 16px; }
  .search-input { background: var(--surface); border: 1px solid var(--border); color: var(--text); padding: 7px 10px; border-radius: 7px; font-size: 12px; font-family: 'DM Sans', sans-serif; width: 200px; }
  .search-input:focus { outline: none; border-color: var(--accent); }
  .filter-select { background: var(--surface); border: 1px solid var(--border); color: var(--text2); padding: 7px 10px; border-radius: 7px; font-size: 12px; cursor: pointer; font-family: 'DM Sans', sans-serif; }

  /* TOAST */
  .toast { position: fixed; bottom: 20px; right: 20px; background: var(--surface2); border: 1px solid var(--border); border-radius: 9px; padding: 10px 14px; font-size: 13px; z-index: 1000; max-width: 300px; animation: slideUp 0.2s ease; box-shadow: 0 8px 32px #00000055; }
  .toast.success { border-color: var(--green); color: var(--green); }
  .toast.error { border-color: var(--red); color: var(--red); }
  .toast.info { border-color: var(--accent); color: var(--accent); }
  @keyframes slideUp { from { transform: translateY(10px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

  /* EMPTY */
  .empty-state { text-align: center; padding: 50px 20px; color: var(--text3); }
  .empty-state-icon { font-size: 36px; margin-bottom: 10px; }
  .empty-state h3 { font-family: 'DM Serif Display', serif; font-size: 18px; color: var(--text2); margin-bottom: 5px; }
  .empty-state p { font-size: 12px; line-height: 1.6; max-width: 300px; margin: 0 auto; }

  /* SETTINGS */
  .settings-grid { display: flex; flex-direction: column; gap: 14px; max-width: 600px; }
  .settings-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 18px; }
  .settings-card h3 { font-size: 13px; font-weight: 600; color: var(--text); margin-bottom: 3px; }
  .settings-card p { font-size: 12px; color: var(--text3); margin-bottom: 12px; line-height: 1.5; }
`;

export default function App() {
  const [page, setPage] = useState('browser');
  const [state, setState] = useState(() => loadState() || DEFAULT_STATE);
  const sources = state.sources && state.sources.length ? state.sources : DEFAULT_SOURCES;
  const [activeSource, setActiveSource] = useState(() => {
    const s = (loadState() || DEFAULT_STATE).sources;
    return (s && s.length ? s : DEFAULT_SOURCES)[0];
  });
  const [toast, setToast] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pipelineStatus, setPipelineStatus] = useState('guardada');
  const [iframeStatus, setIframeStatus] = useState({}); // 'ok' | 'blocked' | 'loading'

  useEffect(() => { saveState(state); }, [state]);
  const SOURCES = state.sources || DEFAULT_SOURCES;

  const showToast = (msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const addJob = (jobData) => {
    const id = Date.now().toString(36);
    const targetCompanies = state.targetCompanies
      ? state.targetCompanies.split(',').map(c => c.trim().toLowerCase()).filter(Boolean)
      : [];
    const isTarget = targetCompanies.some(c => (jobData.company || '').toLowerCase().includes(c));
    const newJob = {
      id,
      title: jobData.title || '',
      company: jobData.company || '',
      url: jobData.url || '',
      location: jobData.location || '',
      salary: jobData.salary || '',
      source: jobData.source || activeSource.name,
      date: new Date().toISOString().slice(0, 10),
      status: 'guardada',
      notes: jobData.notes || '',
      isTarget,
      addedAt: new Date().toISOString(),
    };
    setState(s => ({ ...s, jobs: [newJob, ...s.jobs] }));
    showToast('Oferta guardada en el CRM ✓', 'success');
    setShowAddModal(false);
  };

  const updateJob = useCallback((id, updates) => {
    setState(s => ({ ...s, jobs: s.jobs.map(j => j.id === id ? { ...j, ...updates } : j) }));
  }, []);

  const deleteJob = (id) => {
    setState(s => ({ ...s, jobs: s.jobs.filter(j => j.id !== id) }));
    setSelectedJob(null);
    showToast('Oferta eliminada', 'info');
  };

  // computed
  const activeJobs = state.jobs.filter(j => ['guardada', 'aplicada', 'en_proceso', 'oferta'].includes(j.status));
  const allSources = [...new Set(state.jobs.map(j => j.source))];
  const explorerJobs = state.jobs.filter(j => {
    if (searchTerm && !`${j.title} ${j.company}`.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (statusFilter !== 'all' && j.status !== statusFilter) return false;
    return true;
  });

  // ── URL PARSER ────────────────────────────────────────────────────────────
  const parseJobFromUrl = (url) => {
    try {
      const u = new URL(url);
      const parts = u.pathname.split('/').filter(Boolean);
      let title = '', company = '', source = '';

      // Himalayas: /jobs/company-name/job-title
      if (u.hostname.includes('himalayas.app') && parts.length >= 3) {
        company = parts[1].replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        title = parts[2].replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        source = 'Himalayas';
      }
      // Remotive: /remote-jobs/category/job-title-company
      else if (u.hostname.includes('remotive.com') && parts.length >= 3) {
        const slug = parts[parts.length - 1].replace(/-/g, ' ');
        title = slug.replace(/\b\w/g, c => c.toUpperCase());
        source = 'Remotive';
      }
      // LinkedIn: /jobs/view/job-title-at-company-id
      else if (u.hostname.includes('linkedin.com') && parts.includes('view')) {
        const idx = parts.indexOf('view');
        if (parts[idx + 1]) {
          const slug = parts[idx + 1].replace(/-\d+$/, '').replace(/-/g, ' ');
          title = slug.replace(/\b\w/g, c => c.toUpperCase());
          if (title.toLowerCase().includes(' at ')) {
            const [t, c] = title.split(/ at /i);
            title = t.trim();
            company = c.trim();
          }
        }
        source = 'LinkedIn';
      }
      // Indeed: /viewjob?jk=...&title=...
      else if (u.hostname.includes('indeed.com')) {
        const t = u.searchParams.get('title');
        if (t) title = t.replace(/\b\w/g, c => c.toUpperCase());
        source = 'Indeed';
      }
      // Generic: try to get something readable from the path
      else {
        const slug = parts[parts.length - 1] || '';
        if (slug) title = slug.replace(/-|_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        source = u.hostname.replace('www.', '').split('.')[0];
        source = source.charAt(0).toUpperCase() + source.slice(1);
      }

      return { title, company, source };
    } catch { return { title: '', company: '', source: '' }; }
  };

  // ── ADD MODAL ──────────────────────────────────────────────────────────────
  const AddModal = () => {
    const [form, setForm] = useState({
      title: '', company: '', url: '', location: '', salary: '', notes: '', source: activeSource.name,
    });
    const [parsing, setParsing] = useState(false);

    const handleUrlChange = (url) => {
      setForm(f => ({ ...f, url }));
      if (!url) return;
      try {
        new URL(url); // validate
        setParsing(true);
        setTimeout(() => {
          const parsed = parseJobFromUrl(url);
          setForm(f => ({
            ...f,
            url,
            title: f.title || parsed.title,
            company: f.company || parsed.company,
            source: parsed.source || f.source,
          }));
          setParsing(false);
        }, 300);
      } catch {}
    };

    return (
      <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div className="modal-title">Guardar oferta</div>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowAddModal(false)}>×</button>
          </div>
          <div className="field-group">
            <label className="field-label">URL de la oferta</label>
            <input className="input" value={form.url} onChange={e => handleUrlChange(e.target.value)} placeholder="Pega el link de la oferta aquí…" autoFocus />
            {parsing && <span style={{fontSize:11, color:'var(--accent)', marginTop:4, display:'block'}}>Extrayendo datos…</span>}
          </div>
          <div className="field-group">
            <label className="field-label">Título del puesto *</label>
            <input className="input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Senior Supply Chain Manager" />
          </div>
          <div className="field-group">
            <label className="field-label">Empresa *</label>
            <input className="input" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} placeholder="Nombre de la empresa" />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="field-group" style={{ flex: 1 }}>
              <label className="field-label">Ubicación</label>
              <input className="input" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Remote / EU" />
            </div>
            <div className="field-group" style={{ flex: 1 }}>
              <label className="field-label">Salario</label>
              <input className="input" value={form.salary} onChange={e => setForm(f => ({ ...f, salary: e.target.value }))} placeholder="60k-80k €" />
            </div>
          </div>
          <div className="field-group">
            <label className="field-label">Notas iniciales</label>
            <textarea className="textarea" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Por qué me interesa, próxima acción…" />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button
              className="btn btn-primary"
              style={{ flex: 1 }}
              onClick={() => { if (form.title && form.company) addJob(form); else showToast('Título y empresa son obligatorios', 'error'); }}
            >
              Guardar en CRM
            </button>
            <button className="btn btn-ghost" onClick={() => setShowAddModal(false)}>Cancelar</button>
          </div>
        </div>
      </div>
    );
  };

  // ── JOB DETAIL MODAL ───────────────────────────────────────────────────────
  const JobModal = ({ job }) => {
    const [notes, setNotes] = useState(job.notes || '');
    return (
      <div className="modal-overlay" onClick={() => setSelectedJob(null)}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div className="modal-title">{job.title}</div>
              <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 2 }}>
                {job.company}
                {job.isTarget && <span style={{ color: 'var(--accent)', marginLeft: 8 }}>★ Target</span>}
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setSelectedJob(null)}>×</button>
          </div>
          <div className="job-meta">
            <span className="tag tag-source">{job.source}</span>
            {job.location && <span className="tag tag-source">{job.location}</span>}
            {job.salary && <span className="tag tag-source" style={{ color: 'var(--green)' }}>{job.salary}</span>}
            <span className="tag tag-source">{job.date}</span>
          </div>
          <div className="modal-section">
            <div className="modal-section-label">Estado</div>
            <select
              className="status-select"
              style={{ width: '100%' }}
              value={job.status}
              onChange={e => { updateJob(job.id, { status: e.target.value }); setSelectedJob({ ...job, status: e.target.value }); }}
            >
              {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="modal-section">
            <div className="modal-section-label">Notas</div>
            <textarea
              className="textarea"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onBlur={() => updateJob(job.id, { notes })}
              placeholder="Contacto, próxima acción, impresiones de la entrevista…"
            />
          </div>
          <div className="modal-section" style={{ display: 'flex', gap: 8 }}>
            {job.url && (
              <a href={job.url} target="_blank" rel="noopener noreferrer" style={{ flex: 1 }}>
                <button className="btn btn-primary" style={{ width: '100%' }}>Abrir oferta →</button>
              </a>
            )}
            <button className="btn btn-danger" onClick={() => deleteJob(job.id)}>Eliminar</button>
          </div>
        </div>
      </div>
    );
  };

  // ── JOB CARD ───────────────────────────────────────────────────────────────
  const JobCard = ({ job }) => (
    <div className={`job-card ${job.isTarget ? 'target-company' : ''}`} onClick={() => setSelectedJob(job)}>
      <div className="job-card-top">
        <div>
          <div className="job-title">{job.title}</div>
          <div className="job-company">
            {job.company}
            {job.isTarget && <span style={{ color: 'var(--accent)', marginLeft: 6, fontSize: 11 }}>★ Target</span>}
          </div>
        </div>
        <span className="tag tag-status" style={{ background: STATUS_COLORS[job.status] + '33', color: STATUS_COLORS[job.status], border: `1px solid ${STATUS_COLORS[job.status]}44`, whiteSpace: 'nowrap' }}>
          {STATUS_LABELS[job.status]}
        </span>
      </div>
      <div className="job-meta">
        <span className="tag tag-source">{job.source}</span>
        {job.location && <span className="tag tag-source">{job.location}</span>}
        {job.salary && <span className="tag tag-source" style={{ color: 'var(--green)' }}>{job.salary}</span>}
        <span className="tag tag-source">{job.date}</span>
      </div>
      <div className="job-actions" onClick={e => e.stopPropagation()}>
        <select className="status-select" value={job.status} onChange={e => updateJob(job.id, { status: e.target.value })}>
          {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {job.url && (
          <a href={job.url} target="_blank" rel="noopener noreferrer">
            <button className="btn btn-ghost btn-sm">Abrir →</button>
          </a>
        )}
      </div>
    </div>
  );

  // ── PAGES ──────────────────────────────────────────────────────────────────
  const PageBrowser = () => (
    <div className="browser-panel">
      <div className="browser-toolbar">
        <span style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500, whiteSpace: 'nowrap' }}>{activeSource.name}</span>
        <input className="browser-url" readOnly value={activeSource.url} />
        <button className="btn btn-ghost btn-sm" onClick={() => window.open(activeSource.url, '_blank')}>↗ Abrir</button>
      </div>
      <div className="iframe-container">
        {iframeStatus[activeSource.id] === 'blocked' ? (
          <div className="iframe-blocked">
            <div style={{ fontSize: 48 }}>🔒</div>
            <h3>{activeSource.name} bloquea el acceso embebido</h3>
            <p>Esta web no permite cargarse en paneles externos. Ábrela en una nueva pestaña, encuentra la oferta que te interesa y guárdala en el CRM con el botón de abajo.</p>
            <button className="btn btn-primary" style={{ marginTop: 8 }} onClick={() => { window.open(activeSource.url, '_blank'); setShowAddModal(true); }}>
              Abrir en nueva pestaña + Guardar oferta
            </button>
          </div>
        ) : (
          <>
            {iframeStatus[activeSource.id] !== 'ok' && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', zIndex: 10, flexDirection: 'column', gap: 10, color: 'var(--text3)' }}>
                <div style={{ width: 28, height: 28, border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <span style={{ fontSize: 12 }}>Cargando {activeSource.name}…</span>
              </div>
            )}
            <iframe
              key={activeSource.id}
              src={activeSource.url}
              title={activeSource.name}
              onLoad={() => setIframeStatus(s => ({ ...s, [activeSource.id]: 'ok' }))}
              onError={() => setIframeStatus(s => ({ ...s, [activeSource.id]: 'blocked' }))}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation allow-popups-to-escape-sandbox"
              style={{ opacity: iframeStatus[activeSource.id] === 'ok' ? 1 : 0 }}
            />
          </>
        )}
      </div>
      <div className="save-fab">
        <button className="save-fab-btn" onClick={() => setShowAddModal(true)}>
          + Guardar oferta en CRM
        </button>
      </div>
    </div>
  );

  const PageExplorer = () => (
    <div className="crm-panel">
      <div className="page-header">
        <div>
          <div className="page-title">Explorador</div>
          <div className="page-subtitle">{explorerJobs.length} de {state.jobs.length} ofertas</div>
        </div>
      </div>
      <div className="page-content">
        <div className="toolbar">
          <input className="search-input" placeholder="Buscar…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">Todos los estados</option>
            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {explorerJobs.length === 0
          ? <div className="empty-state"><div className="empty-state-icon">🔍</div><h3>Sin resultados</h3><p>Guarda ofertas desde el navegador.</p></div>
          : <div className="jobs-list">{explorerJobs.map(j => <JobCard key={j.id} job={j} />)}</div>
        }
      </div>
    </div>
  );

  const PagePipeline = () => {
    const filtered = state.jobs.filter(j => j.status === pipelineStatus);
    return (
      <div className="crm-panel">
        <div className="page-header">
          <div>
            <div className="page-title">CRM & Pipeline</div>
            <div className="page-subtitle">{activeJobs.length} candidaturas activas</div>
          </div>
        </div>
        <div className="page-content">
          <div className="stats-row">
            {[['guardada', 'Guardadas'], ['aplicada', 'Aplicadas'], ['en_proceso', 'En Proceso'], ['oferta', 'Ofertas']].map(([s, l]) => (
              <div key={s} className="stat-card">
                <div className="stat-value">{state.jobs.filter(j => j.status === s).length}</div>
                <div className="stat-label">{l}</div>
              </div>
            ))}
          </div>
          <div className="pipeline-tabs">
            {Object.entries(STATUS_LABELS).map(([s, l]) => (
              <button key={s} className={`pipeline-tab ${pipelineStatus === s ? 'active' : ''}`} onClick={() => setPipelineStatus(s)}>
                <span className="status-dot" style={{ background: STATUS_COLORS[s] }} />{l}
              </button>
            ))}
          </div>
          {filtered.length === 0
            ? <div className="empty-state"><div className="empty-state-icon">📋</div><h3>Sin candidaturas en "{STATUS_LABELS[pipelineStatus]}"</h3></div>
            : <div className="jobs-list">{filtered.map(j => <JobCard key={j.id} job={j} />)}</div>
          }
        </div>
      </div>
    );
  };

  const NewSourceForm = ({ onAdd }) => {
    const [name, setName] = useState('');
    const [url, setUrl] = useState('');
    const handleAdd = () => {
      if (!name.trim() || !url.trim()) return;
      let cleanUrl = url.trim();
      if (!cleanUrl.startsWith('http')) cleanUrl = 'https://' + cleanUrl;
      onAdd({ id: Date.now().toString(36), name: name.trim(), url: cleanUrl });
      setName(''); setUrl('');
      showToast(`"${name}" añadida`, 'success');
    };
    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input className="input" style={{ flex: '1 1 140px' }} value={name} onChange={e => setName(e.target.value)} placeholder="Nombre (ej. Jobgether)" />
        <input className="input" style={{ flex: '2 1 220px' }} value={url} onChange={e => setUrl(e.target.value)} placeholder="URL (ej. https://jobgether.com)" onKeyDown={e => e.key === 'Enter' && handleAdd()} />
        <button className="btn btn-primary" onClick={handleAdd} disabled={!name || !url}>Añadir</button>
      </div>
    );
  };

  const PageSettings = () => {
    const [local, setLocal] = useState({ targetCompanies: state.targetCompanies });
    const [newSource, setNewSource] = useState({ name: '', url: '' });
    const [sourceError, setSourceError] = useState('');

    const handleAddSource = () => {
      if (!newSource.name.trim()) { setSourceError('El nombre es obligatorio'); return; }
      if (!newSource.url.trim()) { setSourceError('La URL es obligatoria'); return; }
      try { new URL(newSource.url); } catch { setSourceError('URL no válida — incluye https://'); return; }
      const id = newSource.name.toLowerCase().replace(/\s+/g, '-') + '-' + Date.now();
      setState(st => ({ ...st, sources: [...(st.sources || DEFAULT_SOURCES), { id, name: newSource.name.trim(), url: newSource.url.trim() }] }));
      setNewSource({ name: '', url: '' });
      setSourceError('');
      showToast(newSource.name + ' añadida', 'success');
    };

    return (
      <div className="crm-panel">
        <div className="page-header">
          <div><div className="page-title">Configuración</div></div>
        </div>
        <div className="page-content">
          <div className="settings-grid">
            <div className="settings-card">
              <h3>Empresas target</h3>
              <p>Aparecen destacadas con ★ en el CRM.</p>
              <textarea className="textarea" value={local.targetCompanies} onChange={e => setLocal(l => ({ ...l, targetCompanies: e.target.value }))} placeholder="Empresa A, Empresa B…" />
            </div>
            <div className="settings-card">
              <h3>Fuentes de búsqueda</h3>
              <p>Verde = accesible embebida · Rojo = bloqueada · Gris = sin probar. Añade cualquier web de empleo que uses.</p>
              {(state.sources || DEFAULT_SOURCES).map(s => {
                const status = iframeStatus[s.id];
                const color = status === 'ok' ? 'var(--green)' : status === 'blocked' ? 'var(--red)' : 'var(--text3)';
                const label = status === 'ok' ? 'Accesible' : status === 'blocked' ? 'Bloqueada' : 'Sin probar';
                return (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                    <span className="source-dot" style={{ background: color }} />
                    <span style={{ fontSize: 13, flex: 1 }}>{s.name}</span>
                    <span style={{ fontSize: 11, color, minWidth: 60 }}>{label}</span>
                    <button className="btn btn-ghost btn-sm" onClick={() => window.open(s.url, '_blank')}>↗</button>
                    <button
                      className="btn btn-sm"
                      style={{ background: 'transparent', color: 'var(--red)', border: '1px solid var(--red)', padding: '3px 8px', fontSize: 11 }}
                      onClick={() => {
                        if (window.confirm(`¿Eliminar "${s.name}"?`)) {
                          setState(st => ({ ...st, sources: (st.sources || DEFAULT_SOURCES).filter(x => x.id !== s.id) }));
                          if (activeSource.id === s.id) setActiveSource((state.sources || DEFAULT_SOURCES)[0]);
                        }
                      }}
                    >✕</button>
                  </div>
                );
              })}
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500, marginBottom: 8 }}>Añadir nueva fuente</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <input className="input" style={{ flex: '0 0 140px' }} placeholder="Nombre" value={newSource.name} onChange={e => setNewSource(n => ({ ...n, name: e.target.value }))} onKeyDown={e => e.key === 'Enter' && handleAddSource()} />
                  <input className="input" style={{ flex: 1 }} placeholder="https://ejemplo.com/jobs" value={newSource.url} onChange={e => setNewSource(n => ({ ...n, url: e.target.value }))} onKeyDown={e => e.key === 'Enter' && handleAddSource()} />
                  <button className="btn btn-primary btn-sm" onClick={handleAddSource}>Añadir</button>
                </div>
                {sourceError && <div style={{ fontSize: 11, color: 'var(--red)' }}>{sourceError}</div>}
              </div>
            </div>
            <button className="btn btn-primary" style={{ width: 'fit-content' }} onClick={() => { setState(s => ({ ...s, ...local })); showToast('Guardado', 'success'); }}>
              Guardar configuración
            </button>
          </div>
        </div>
      </div>
    );
  };

  const PAGES = { browser: <PageBrowser />, explorer: <PageExplorer />, pipeline: <PagePipeline />, settings: <PageSettings /> };

  return (
    <>
      <style>{CSS}</style>
      <div className="layout">
        <nav className="sidebar">
          <div className="sidebar-logo">
            <h1>JobHunter</h1>
            <p>Senior · Remote</p>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-label">Navegar</div>
            {sources.map(s => {
              const status = iframeStatus[s.id];
              const dotColor = status === 'ok' ? 'var(--green)' : status === 'blocked' ? 'var(--red)' : status === 'loading' ? 'var(--orange)' : 'var(--text3)';
              return (
                <button
                  key={s.id}
                  className={`nav-item ${page === 'browser' && activeSource.id === s.id ? 'active' : ''}`}
                  onClick={() => { setActiveSource(s); setPage('browser'); if (!iframeStatus[s.id]) setIframeStatus(st => ({ ...st, [s.id]: 'loading' })); }}
                >
                  <span className="source-dot" style={{ background: dotColor }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                </button>
              );
            })}
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-label">CRM</div>
            {[
              { id: 'explorer', icon: '🔍', label: 'Explorador', badge: null },
              { id: 'pipeline', icon: '📋', label: 'Pipeline', badge: activeJobs.length || null },
              { id: 'settings', icon: '⚙️', label: 'Configuración', badge: null },
            ].map(item => (
              <button key={item.id} className={`nav-item ${page === item.id ? 'active' : ''}`} onClick={() => setPage(item.id)}>
                <span>{item.icon}</span><span>{item.label}</span>
                {item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
              </button>
            ))}
          </div>
          <div style={{ marginTop: 'auto', padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              {state.jobs.length} ofertas guardadas<br />
              {state.jobs.filter(j => j.status === 'aplicada').length} aplicadas
            </div>
          </div>
        </nav>
        <main className="main">{PAGES[page]}</main>
      </div>
      {showAddModal && <AddModal />}
      {selectedJob && <JobModal job={selectedJob} />}
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}
